import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Alert,
  Tooltip,
  useTheme,
  type SelectChangeEvent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import DraftsIcon from '@mui/icons-material/Drafts';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { format } from 'date-fns';
import {
  useInspectionStats,
  usePagedInspections,
  type InspectionListItem,
  type InspectionListSortField,
  type InspectionPageFilters,
} from '@/hooks/useInspections';
import { useMechanics } from '@/hooks/useStaffUsers';
import { useDebounce } from '@/hooks/useDebounce';
import { useUrlSearchParam } from '@/hooks/useUrlSearchParam';
import { useNotify } from '@/stores/notificationStore';
import { DataTable, EntityListResults, type DataTableColumn, RegistrationPlate } from '@/components/common';
import { neutralGrey } from '@/styles/theme';
import type { InspectionStatus } from '@/types';
import { useUrlMultiSelectFilter } from '@/hooks/useUrlMultiSelectFilter';
import { dateFnsLocale } from '@/utils/locale';

const statusConfig: Record<InspectionStatus, { labelKey: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' }> = {
  draft: { labelKey: 'status.draft', color: 'default' },
  pending: { labelKey: 'status.pending', color: 'default' },
  in_progress: { labelKey: 'status.inProgress', color: 'warning' },
  completed: { labelKey: 'status.completed', color: 'success' },
  sent_to_customer: { labelKey: 'status.sent', color: 'info' },
  approved: { labelKey: 'status.approved', color: 'success' },
  rejected: { labelKey: 'status.rejected', color: 'error' },
};

const STATUS_ICONS: Record<string, React.ElementType> = {
  draft: DraftsIcon,
  in_progress: BuildIcon,
  completed: CheckCircleIcon,
  sent_to_customer: HourglassEmptyIcon,
  approved: ThumbUpIcon,
  rejected: ThumbDownIcon,
};

const ALL_STATUSES: InspectionStatus[] = ['draft', 'in_progress', 'completed', 'sent_to_customer', 'approved', 'rejected'];

const EMPTY_INSPECTION_ROWS: InspectionListItem[] = [];

/**
 * Inspection-row columns the server can order. `totalIssues`, `status` and
 * `createdAt` live on the inspection. Vehicle and customer names are joins,
 * so they sort only the loaded page.
 */
const SERVER_SORT_API = {
  issues: 'totalIssues',
  status: 'status',
  date: 'createdAt',
} as const satisfies Record<string, InspectionListSortField>;

type ServerColumnKey = keyof typeof SERVER_SORT_API;
type PageLocalSortKey = 'vehicle' | 'customer';

function isServerColumn(key: string): key is ServerColumnKey {
  return Object.prototype.hasOwnProperty.call(SERVER_SORT_API, key);
}

function isPageLocalSortKey(key: string): key is PageLocalSortKey {
  return key === 'vehicle' || key === 'customer';
}

function comparePageLocal(a: InspectionListItem, b: InspectionListItem, key: PageLocalSortKey): number {
  if (key === 'vehicle') {
    return (a.vehicle.registrationNumber ?? '').localeCompare(b.vehicle.registrationNumber ?? '');
  }
  return `${a.customer.firstName ?? ''} ${a.customer.lastName ?? ''}`.localeCompare(
    `${b.customer.firstName ?? ''} ${b.customer.lastName ?? ''}`,
    'fi',
  );
}

function serverColumnKey(sortBy: string | undefined): ServerColumnKey {
  const match = (Object.entries(SERVER_SORT_API) as [ServerColumnKey, InspectionListSortField][]).find(
    ([, apiField]) => apiField === sortBy,
  );
  return match?.[0] ?? 'date';
}

export default function InspectionsPage() {
  const { t } = useTranslation('inspection');
  const navigate = useNavigate();
  const notify = useNotify();
  const theme = useTheme();

  // Inspection status → brand semantic color (mirrors the fixed status spectrum).
  const statusColorMap: Record<string, string> = {
    draft: neutralGrey,
    in_progress: theme.palette.warning.main,
    completed: theme.palette.success.main,
    sent_to_customer: theme.palette.secondary.main,
    approved: theme.palette.secondary.dark,
    rejected: theme.palette.error.main,
  };

  /*
    #878 extracted this into useUrlMultiSelectFilter — but nothing ever
    called the hook, so all four pages kept their own verbatim copy of
    ~50 lines (#2726). This is that migration, finally.
  */
  const { selectedFilters, toggleFilter, allSelected } = useUrlMultiSelectFilter({
    allFilters: ALL_STATUSES,
    storageKey: 'vs-filter-inspections',
  });

  // Search lives in the URL so Back restores it (#4112).
  const [searchQuery, setSearchQuery] = useUrlSearchParam();
  const debouncedQuery = useDebounce(searchQuery, 300);

  // Mechanic filter
  const [mechanicFilter, setMechanicFilter] = useState<string | 'all'>('all');
  const [pageLocalSort, setPageLocalSort] = useState<{
    key: PageLocalSortKey;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Dashboard stats (always active)
  const { data: stats } = useInspectionStats();

  const listFilters = useMemo((): InspectionPageFilters => {
    const next: InspectionPageFilters = {};
    if (debouncedQuery) next.q = debouncedQuery;
    if (mechanicFilter !== 'all') next.mechanicId = mechanicFilter;
    // Every chip on means "no status filter", including statuses that have no
    // chip (pending). A subset is sent so the page total matches the chips.
    if (!allSelected) {
      const statuses = selectedFilters.filter((status) =>
        (ALL_STATUSES as readonly string[]).includes(status),
      );
      if (statuses.length > 0) next.status = statuses.join(',');
    }
    return next;
  }, [debouncedQuery, mechanicFilter, allSelected, selectedFilters]);

  const noneSelected = selectedFilters.length === 0;

  const {
    rows: fetchedRows,
    total: fetchedTotal,
    sort,
    setSort,
    isLoading: listLoading,
    isError: listFailed,
    error: listError,
    refetch: refetchList,
    pagination: listPagination,
  } = usePagedInspections(listFilters, { enabled: !noneSelected });

  const rows = noneSelected ? EMPTY_INSPECTION_ROWS : fetchedRows;
  const total = noneSelected ? 0 : fetchedTotal;
  const pagination = noneSelected ? { ...listPagination, total: 0, page: 0 } : listPagination;
  const listIsLoading = !noneSelected && listLoading;
  const listIsError = !noneSelected && listFailed;

  const { data: mechanics = [] } = useMechanics();

  // Status count map from stats
  const statusCountMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const entry of stats?.byStatus ?? []) {
      map[entry.status] = entry.count;
    }
    return map;
  }, [stats]);

  // Handlers
  const handleNewInspection = () => {
    notify.info(t('newInspection'), t('selectWorkOrderForInspection'));
  };

  const handleSendToCustomer = (_inspectionId: string) => {
    notify.success(t('sent'), t('reportSentToCustomer'));
  };

  // Price formatter
  const formatPrice = (cents: number) => {
    const euros = Number(cents) / 100;
    return `€${euros.toFixed(2)}`;
  };

  const displayedRows = useMemo(() => {
    if (!pageLocalSort) return rows;
    const { key, direction } = pageLocalSort;
    const sorted = [...rows].sort((a, b) => comparePageLocal(a, b, key));
    return direction === 'asc' ? sorted : sorted.reverse();
  }, [rows, pageLocalSort]);

  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    if (isPageLocalSortKey(key)) {
      setPageLocalSort({ key, direction });
      return;
    }
    if (!isServerColumn(key)) return;
    setPageLocalSort(null);
    setSort({ sortBy: SERVER_SORT_API[key], sortDir: direction });
  };

  const tableSortKey = pageLocalSort?.key ?? serverColumnKey(sort?.sortBy);
  const tableSortDirection = pageLocalSort?.direction ?? sort?.sortDir ?? 'desc';

  const columns: DataTableColumn<InspectionListItem>[] = [
    {
      header: t('workOrder'),
      renderCell: (inspection) => (
        <Typography variant="body2" fontWeight={600}>
          {inspection.booking.serviceType ?? '-'}
        </Typography>
      ),
    },
    {
      header: `${t('vehicle')} (${t('sort.thisPage')})`,
      sortKey: 'vehicle',
      renderCell: (inspection) => (
        <RegistrationPlate registrationNumber={inspection.vehicle.registrationNumber ?? '-'} />
      ),
    },
    {
      header: `${t('customer')} (${t('sort.thisPage')})`,
      sortKey: 'customer',
      renderCell: (inspection) => (
        <Typography variant="body2">
          {inspection.customer.firstName} {inspection.customer.lastName}
        </Typography>
      ),
    },
    {
      header: t('issues'),
      sortKey: 'issues',
      renderCell: (inspection) =>
        inspection.totalIssues > 0 ? (
          <Chip
            size="small"
            color={inspection.totalCritical > 0 ? 'error' : 'warning'}
            label={inspection.totalIssues}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      header: t('statusLabel'),
      sortKey: 'status',
      renderCell: (inspection) => {
        const status = inspection.status as InspectionStatus;
        const statusInfo = statusConfig[status] ?? { labelKey: status, color: 'default' as const };
        return (
          <Chip
            size="small"
            label={t(statusInfo.labelKey)}
            color={statusInfo.color}
          />
        );
      },
    },
    {
      header: t('mechanic'),
      renderCell: (inspection) => (
        <Typography variant="body2">
          {inspection.mechanic.firstName} {inspection.mechanic.lastName}
        </Typography>
      ),
    },
    {
      header: t('recommendations'),
      renderCell: (inspection) =>
        inspection.totalRecommendationsCents > 0 ? (
          <Typography variant="body2" fontWeight={500}>
            {formatPrice(inspection.totalRecommendationsCents)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      header: t('date'),
      sortKey: 'date',
      renderCell: (inspection) => (
        <Typography variant="body2" color="text.secondary">
          {format(new Date(inspection.createdAt), 'd.M.yyyy', { locale: dateFnsLocale() })}
        </Typography>
      ),
    },
    {
      header: '',
      align: 'right',
      renderCell: (inspection) => {
        const status = inspection.status as InspectionStatus;
        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
            {(status === 'draft' || status === 'in_progress') && (
              <Tooltip title={t('edit')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/inspections/${inspection.id}/edit`);
                  }}
                  aria-label={t('edit')}
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {status === 'completed' && (
              <Tooltip title={t('sendToCustomer')}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSendToCustomer(inspection.id);
                  }}
                  aria-label={t('sendToCustomer')}
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('view')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/inspections/${inspection.id}`);
                }}
                aria-label={t('view')}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const renderMobileCard = (inspection: InspectionListItem) => {
    const status = inspection.status as InspectionStatus;
    const statusInfo = statusConfig[status] ?? { labelKey: status, color: 'default' as const };
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {inspection.booking.serviceType ?? '-'}
          </Typography>
          <RegistrationPlate registrationNumber={inspection.vehicle.registrationNumber ?? '-'} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Chip
            size="small"
            label={t(statusInfo.labelKey)}
            color={statusInfo.color}
          />
          {inspection.totalIssues > 0 && (
            <Chip size="small" color={inspection.totalCritical > 0 ? 'error' : 'warning'} label={`${inspection.totalIssues} ${t('issues')}`} />
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {inspection.mechanic.firstName} {(inspection.mechanic.lastName ?? '').charAt(0)}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {format(new Date(inspection.createdAt), 'd.M.yyyy', { locale: dateFnsLocale() })}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {/* Header: title + add button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight={600}>
          {t('title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewInspection}
        >
          {t('newInspection')}
        </Button>
      </Box>

      {/* Search bar */}
      <TextField
        size="small"
        placeholder={t('searchPlaceholder')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ width: { xs: '100%', md: 500 }, mb: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <Tooltip title={t('clearSearch')}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    aria-label={t('clearSearch')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          },
        }}
      />

      {/* Multi-select status chip toggles + mechanic filter */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        {ALL_STATUSES.map((statusKey) => {
          const isSelected = selectedFilters.includes(statusKey);
          const count = statusCountMap[statusKey] ?? 0;
          const color = statusColorMap[statusKey] ?? neutralGrey;
          const IconComp = STATUS_ICONS[statusKey] ?? HourglassEmptyIcon;

          return (
            <Tooltip key={statusKey} title={t(`status.${statusKey === 'in_progress' ? 'inProgress' : statusKey === 'sent_to_customer' ? 'sent' : statusKey}`)}>
              <Chip
                icon={<IconComp sx={{ fontSize: 16 }} />}
                label={count}
                size="small"
                onClick={() => toggleFilter(statusKey)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  minWidth: 52,
                  ...(isSelected
                    ? {
                        bgcolor: color,
                        color: 'common.white',
                        '& .MuiChip-icon': { color: 'common.white' },
                        '&:hover': { bgcolor: color, opacity: 0.85 },
                      }
                    : {
                        bgcolor: 'transparent',
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.secondary',
                        '& .MuiChip-icon': { color },
                        '&:hover': { bgcolor: 'action.hover' },
                      }),
                }}
              />
            </Tooltip>
          );
        })}

        <Box sx={{ flex: 1 }} />

        {/* Mechanic filter */}
        <FormControl size="small" sx={{ minWidth: { xs: 120, md: 200 } }}>
          <InputLabel>{t('mechanic')}</InputLabel>
          <Select
            value={mechanicFilter}
            label={t('mechanic')}
            onChange={(e: SelectChangeEvent) => setMechanicFilter(e.target.value)}
          >
            <MenuItem value="all">{t('allMechanics')}</MenuItem>
            {mechanics.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <EntityListResults
        loading={listIsLoading}
        isError={listIsError}
        errorText={t('errorLoading')}
        error={listError}
        onRefetch={() => {
          void refetchList();
        }}
        resultCountText={t('searchResultCount', { count: total })}
        showSelectStatusWarning={noneSelected}
        pagination={pagination}
      >
        {pageLocalSort && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="inspection-page-sort-note">
            {t('sort.pageOnly')}
          </Alert>
        )}
        <DataTable<InspectionListItem>
          columns={columns}
          data={displayedRows}
          getRowKey={(insp) => insp.id}
          sortKey={tableSortKey}
          sortDirection={tableSortDirection}
          onSortChange={handleSortChange}
          onRowClick={(inspection) => navigate(`/inspections/${inspection.id}`)}
          getRowHref={(inspection) => `/inspections/${inspection.id}`}
          emptyMessage={t('noInspections')}
          emptyDescription={t('noInspectionsDesc')}
          loading={listIsLoading}
          renderMobileCard={renderMobileCard}
        />
      </EntityListResults>
    </Box>
  );
}
